import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { FolderSimple, Plus, Trash, ArrowRight, MagnifyingGlass } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/projects', newProject);
      setDialogOpen(false);
      setNewProject({ name: '', description: '' });
      navigate(`/projects/${res.data.project_id}`);
    } catch (err) {
      console.error('Failed to create project:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this project and all its files?')) return;
    try {
      await api.delete(`/projects/${projectId}`);
      setProjects(prev => prev.filter(p => p.project_id !== projectId));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.description || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 md:p-8" data-testid="projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A00]">Management</p>
          <h1 className="font-sans text-3xl md:text-4xl font-black tracking-tight mt-1">Projects</h1>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="create-project-btn"
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-6 py-3 font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none shadow-[4px_4px_0px_rgba(0,0,0,1)]"
            >
              <Plus size={16} weight="bold" /> New Project
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-none border border-black/15 bg-white">
            <DialogHeader>
              <DialogTitle className="font-sans text-xl font-bold tracking-tight">Create New Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-6 mt-4">
              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#5E5E5E] mb-2">Project Name</label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  required
                  data-testid="project-name-input"
                  className="w-full bg-transparent border-b border-black/30 focus:border-black px-0 py-3 font-mono text-sm focus:outline-none transition-colors"
                  placeholder="e.g., Villa Project - Phase 1"
                />
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-wider text-[#5E5E5E] mb-2">Description</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  data-testid="project-description-input"
                  className="w-full bg-transparent border border-black/15 focus:border-black p-3 font-mono text-sm focus:outline-none transition-colors resize-none h-24"
                  placeholder="Brief description of the project..."
                />
              </div>
              <button
                type="submit"
                disabled={creating}
                data-testid="create-project-submit-btn"
                className="w-full bg-black text-white py-3 px-6 font-mono text-sm uppercase tracking-wide hover:bg-black/80 transition-colors disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Project'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8F8F8F]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="project-search-input"
          className="w-full bg-white border border-black/15 pl-10 pr-4 py-3 font-mono text-sm focus:outline-none focus:border-black transition-colors"
          placeholder="Search projects..."
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/15">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-48 bg-white animate-pulse border-r border-black/15" />
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/15">
          {filtered.map((project, i) => (
            <Link
              key={project.project_id}
              to={`/projects/${project.project_id}`}
              data-testid={`project-card-${project.project_id}`}
              className={`p-6 bg-white hover:bg-[#F7F7F7] transition-colors group ${
                (i + 1) % 3 !== 0 ? 'md:border-r border-black/15' : ''
              } ${i < filtered.length - (filtered.length % 3 || 3) ? 'border-b border-black/15' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 bg-[#F7F7F7] border border-black/15 flex items-center justify-center group-hover:bg-[#FF5A00]/10 group-hover:border-[#FF5A00]/30 transition-colors">
                  <FolderSimple size={20} weight="regular" className="text-[#5E5E5E] group-hover:text-[#FF5A00] transition-colors" />
                </div>
                <button
                  onClick={(e) => handleDelete(e, project.project_id)}
                  data-testid={`delete-project-${project.project_id}`}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:text-[#FF2B2B] text-[#8F8F8F] transition-all"
                >
                  <Trash size={16} weight="regular" />
                </button>
              </div>
              <h3 className="font-sans text-lg font-bold tracking-tight mt-4 mb-1">{project.name}</h3>
              <p className="font-mono text-xs text-[#8F8F8F] mb-4 line-clamp-2">{project.description || 'No description'}</p>
              <div className="flex items-center justify-between mt-auto pt-4 border-t border-black/10">
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#5E5E5E]">
                  {project.file_count || 0} files
                </span>
                <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                  project.boq_status === 'generated' ? 'bg-[#00A859]/10 text-[#00A859]' :
                  'bg-black/5 text-[#8F8F8F]'
                }`}>
                  {project.boq_status}
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-black/15 p-16 text-center">
          <FolderSimple size={56} weight="regular" className="text-[#8F8F8F] mx-auto mb-4" />
          <p className="font-sans text-xl font-bold mb-2">
            {search ? 'No Projects Found' : 'No Projects Yet'}
          </p>
          <p className="font-mono text-sm text-[#5E5E5E] mb-6">
            {search ? 'Try a different search term' : 'Create your first project to start analyzing drawings'}
          </p>
          {!search && (
            <button
              onClick={() => setDialogOpen(true)}
              data-testid="empty-create-project-btn"
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 font-mono text-sm uppercase tracking-wide hover:bg-black/80 transition-colors"
            >
              <Plus size={16} weight="bold" /> Create Project
            </button>
          )}
        </div>
      )}
    </div>
  );
}
